import * as React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import axios from 'axios'

import {t} from '@lib/helpers'
import {IconArrowDown, IconArrowRight} from '@components/Icon'

const MainLayout = dynamic(() => import('@components/MainLayout'))
const DualSlide = dynamic(() => import('@components/DualSlide'))
const GetStarted = dynamic(() => import('@components/GetStarted'))
const Meta = dynamic(() => import('@components/Meta'))

export default function Careers({jobs}) {
  const benefits = [
    'Hybrid work',
    'Flexible working hours',
    'Health insurance',
    'Mental health and wellness services',
    'Annual health check',
    'Laptop and other equipments',
    'Grab transportation credits',
    'Free snacks & drinks',
    'Weekly massage',
    'Yoga class',
    'Education allowance',
    'Performance bonus',
  ]

  return (
    <React.Fragment>
      <Meta title={`${t('pages.careers.title')} – ${t('layout.title')}`} />

      <div className='Careers'>
        <DualSlide
          height='150vh'
          initValue={[0, -100]}
          conditions={({ratio, tlySet}) => {
            if (ratio <= 0) {
              tlySet([0, -100])
            } else if (ratio > 0 && ratio <= 0.25) {
              tlySet([-100, 0])
            }
          }}
        >
          <div
            className={classNames('DualSlide__wrapper', {
              'DualSlide__wrapper--h1': true,
              'DualSlide__wrapper--h1--red': true,
            })}
          >
            <div className='DualSlide__wrapper__content'>
              <h2 className='DualSlide__h2'>
                <span>เราเติบโตไป</span>
                <br />
                พร้อมกับคุณ
              </h2>

              <p className='DualSlide__p'>
                <strong>Data Wow</strong> มุ่งเน้นการสร้าง พัฒนา และเทรนโมเดล AI
                ทั้ง Machine Learning และ Deep Learning
                ไม่ว่าจะเป็นความแม่นยำของ Data Validation
                การตรวจสอบความถูกต้องของข้อมูล พร้อมทั้งความปลอดภัยของข้อมูล
                สร้างความไว้วางใจ
                ในการบริหารจัดการข้อมูลในทุกรูปแบบที่มาพร้อมความหลากหลายเพื่อรองรับประสิทธิภาพที่ดียิ่งกว่าไปกับเรา
                ด้วยเทคโนโลยีอัจฉริยะ{' '}
                <strong>Intelligent Composable Business</strong>
              </p>

              <div className='Buttons'>
                <a
                  className='DualSlide__a'
                  href='#info'
                  onClick={(e) => {
                    e.preventDefault()

                    window.history.pushState(null, null, '#info')
                    document
                      .querySelector('#info')
                      .scrollIntoView({behavior: 'smooth'})
                  }}
                >
                  <IconArrowDown />
                </a>
              </div>
            </div>
          </div>

          <div
            className={classNames('DualSlide__wrapper', {
              'DualSlide__wrapper--alt': true,
              'DualSlide__wrapper--red': true,
            })}
          >
            <div className='DualSlide__wrapper__content'>
              <h2 className='DualSlide__h2'>
                ร่วมสร้างสิ่งใหม่ๆ ไปด้วยกันกับพวกเรา
              </h2>

              <p className='DualSlide__p'>
                สร้างสรรค์เทคโนโลยีใหม่ๆ เติมความเป็นอัจฉริยะพัฒนาระบบ AI
                ไม่ว่าจะเป็น โมเดล Machine Learning / Deep Learning หรือจะเป็น
                Data Validation การตรวจสอบข้อมูล บริการ Micro-Task Platform,
                บริการจัดหมวดหมู่ข้อมูล Data Categorisation
                รวมทั้งบริการยืนยันตัวตนลูกค้าหรือ Know-Your-Customer (KYC)
                เพิ่มประสิทธิภาพในการนำข้อมูลไปใช้ให้เกิดประโยชน์
              </p>
            </div>
          </div>

          <div className='DualSlide__image DualSlide__content--9'>
            <img
              src='/images/careers/feature2.jpg'
              srcSet='/images/careers/feature2@2x.jpg 2x'
              alt='Think about things differently'
              className='DualSlide__image__img'
              width='719'
              height='850'
              loading='lazy'
            />
          </div>

          <div className='DualSlide__image DualSlide__content--8'>
            <img
              src='/images/careers/feature1.jpg'
              srcSet='/images/careers/feature1@2x.jpg 2x'
              alt='People working'
              className='DualSlide__image__img'
              width='719'
              height='850'
              loading='lazy'
            />
          </div>
        </DualSlide>

        <div className='Careers__video'>
          <div className='Careers__video__aspect-ratio'>
            <iframe
              src='https://www.youtube.com/embed/auQe8TAG6z8'
              title='YouTube video player'
              frameBorder='0'
              allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
              allowFullScreen
            />
          </div>
          <h2>
            Life at <span>Data Wow</span>
          </h2>
        </div>

        <div className='Anchor' id='info'></div>

        <div className='container'>
          {/* benefits */}
          <section className='page-section'>
            <div className='Careers__benefits'>
              <img
                alt='Careers benefits'
                src='/images/careers/benefits.svg'
                width='491'
                height='491'
              />

              <div>
                <h2>
                  <span>เติมเต็มกับคุณภาพชีวิต</span>และศักยภาพของทีม
                </h2>
                <h4>ด้วยสวัสดิการดีๆ จาก Data Wow</h4>

                <div className='Careers__benefits__list'>
                  <ul>
                    {benefits.slice(0, 6).map((value, i) => (
                      <li key={i}>{value}</li>
                    ))}
                  </ul>
                  <ul>
                    {benefits.slice(6).map((value, i) => (
                      <li key={i}>{value}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* position */}
          <section className='page-section'>
            <div className='Careers__positions'>
              <h2>ตำแหน่งที่เปิดรับ</h2>

              <div className='Careers__positions__notice'>
                <Link href='/career-policy'>
                  นโยบายความเป็นส่วนตัวสำหรับผู้สมัครงาน
                </Link>
              </div>

              <ul>
                {jobs.map((job) => (
                  <li key={job.id}>
                    <a
                      href={job.url}
                      title={job.title}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='Careers__positions__links'
                    >
                      <span>{job.title}</span>
                      <IconArrowRight />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        </div>

        <GetStarted />
      </div>
    </React.Fragment>
  )
}

Careers.propTypes = {
  jobs: PropTypes.arrayOf(PropTypes.object),
}

export const getServerSideProps = async (_context) => {
  let jobs = []

  try {
    const response = await axios.get(
      'https://datawow.workable.com/spi/v3/jobs?state=published',
      {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${process.env.WORKABLE_ACCESS_TOKEN}`,
        },
      }
    )

    if (response.status == 200) {
      jobs = response.data.jobs
    }
  } catch (e) {}

  return {
    props: {
      jobs,
    },
  }
}

Careers.getLayout = function getLayout(page) {
  return <MainLayout>{page}</MainLayout>
}
